import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {THUNK_STATUS, Transcription} from '../../domain/types';
import {
  fetchTranscriptions,
  deleteTranscription,
  startTranscription,
  pollTranscriptionStatus,
} from './transcriptionsThunks';

interface TranscriptionsState {
    selectedTranscription: Transcription | null,
    transcriptions: Transcription[];
    loading: boolean;
    error: string | null;
    lastFetched: number | null; // Timestamp of last fetch for cache management
    transcribingOriginPath: string | null; // Where the running job was started from
  api: {
    status: THUNK_STATUS;
  }
}

const initialState: TranscriptionsState = {
  selectedTranscription: null,
  transcriptions: [],
  loading: false,
  error: null,
  lastFetched: null,
  transcribingOriginPath: null,
  api: {
    status: THUNK_STATUS.idle
  }
};

/** Replaces a row in place, or prepends it if the list has not seen it yet. */
function upsert(transcriptions: Transcription[], updated: Transcription): Transcription[] {
  const index = transcriptions.findIndex(t => t.id === updated.id);

  if (index === -1) {
    return [updated, ...transcriptions];
  }

  const next = [...transcriptions];
  next[index] = updated;
  return next;
}

const transcriptionsSlice = createSlice({
  name: 'transcriptions',
  initialState,
  reducers: {
    setSelectedTranscription: (state, action: PayloadAction<Transcription>) => {
      state.selectedTranscription = action.payload;
    },
    clearSelectedTranscription: (state) => {
      state.selectedTranscription = null;
    },
    setTranscriptions: (state, action: PayloadAction<Transcription[]>) => {
      state.transcriptions = action.payload;
      state.error = null;
    },
    addTranscription: (state, action: PayloadAction<Transcription>) => {
      state.transcriptions.push(action.payload);
    },
    removeTranscription: (state, action: PayloadAction<string>) => {
      state.transcriptions = state.transcriptions.filter(t => t.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    invalidateCache: (state) => {
            // Force next fetch to get fresh data
      state.lastFetched = null;
    },
    setTranscribingOriginPath: (state, action: PayloadAction<string | null>) => {
      state.transcribingOriginPath = action.payload;
    },
  },
  extraReducers: (builder) => {
        // Fetch Transcriptions
    builder.addCase(fetchTranscriptions.pending, (state) => {
      state.api.status = THUNK_STATUS.pending;
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTranscriptions.fulfilled, (state, action) => {
      state.api.status = THUNK_STATUS.fulfilled;
      state.transcriptions = action.payload;
      state.loading = false;
      state.error = null;
      state.lastFetched = Date.now();
    });
    builder.addCase(fetchTranscriptions.rejected, (state, action) => {
      state.api.status = THUNK_STATUS.rejected;
      state.loading = false;
      state.error = action.payload as string;
    });

        // Delete Transcription
    builder.addCase(deleteTranscription.pending, (state) => {
      state.api.status = THUNK_STATUS.pending;
      state.loading = true;
    });
    builder.addCase(deleteTranscription.fulfilled, (state, action) => {
      state.api.status = THUNK_STATUS.fulfilled;
            // Filter by csvR2Key (R2 key) since that's what we're deleting
      state.transcriptions = state.transcriptions.filter(t => t.csvR2Key !== action.payload);
      state.loading = false;
            // Invalidate cache after delete so next fetch gets fresh data
      state.lastFetched = null;
    });
    builder.addCase(deleteTranscription.rejected, (state, action) => {
      state.api.status = THUNK_STATUS.rejected;
      state.loading = false;
      state.error = action.payload as string;
    });

    // Start Transcription — the row lands in the list immediately, as `processing`. That row is
    // the progress UI; there is no separate client-side job state to keep in step with it.
    builder.addCase(startTranscription.fulfilled, (state, action) => {
      state.transcriptions = upsert(state.transcriptions, action.payload);
      state.lastFetched = null;
    });

    // Poll — the same row coming back finished, failed, or unchanged.
    builder.addCase(pollTranscriptionStatus.fulfilled, (state, action) => {
      state.transcriptions = upsert(state.transcriptions, action.payload);

      // Keep the Convert page's selection in step: it needs the r2_key that only exists now.
      if (state.selectedTranscription?.id === action.payload.id) {
        state.selectedTranscription = action.payload;
      }

      if (action.payload.status !== 'processing') {
        state.lastFetched = null;
        state.transcribingOriginPath = null;
      }
    });
  },
});

export const {
  setSelectedTranscription,
  clearSelectedTranscription,
  addTranscription,
  invalidateCache,
  setTranscribingOriginPath,
} = transcriptionsSlice.actions;
export default transcriptionsSlice.reducer;
